/**
 * Centralised GA4 ecommerce event helper.
 *
 * Push policy (all enforced here so callers can stay simple):
 *   - currency must be a valid ISO 4217 code
 *   - value must be a finite number
 *   - every item must have a non-empty item_id
 *   - all events in the same session must use a single currency
 *   - optional fields with null/undefined/'' are stripped before push
 *
 * If validation fails the event is suppressed silently — never thrown — so a
 * broken analytics payload can't take down a checkout flow.
 */

const ISO_4217 = new Set([
    "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN",
    "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BOV",
    "BRL", "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHE", "CHF",
    "CHW", "CLF", "CLP", "CNY", "COP", "COU", "CRC", "CUC", "CUP", "CVE",
    "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", "ERN", "ETB", "EUR", "FJD",
    "FKP", "GBP", "GEL", "GHS", "GIP", "GMD", "GNF", "GTQ", "GYD", "HKD",
    "HNL", "HTG", "HUF", "IDR", "ILS", "INR", "IQD", "IRR", "ISK", "JMD",
    "JOD", "JPY", "KES", "KGS", "KHR", "KMF", "KPW", "KRW", "KWD", "KYD",
    "KZT", "LAK", "LBP", "LKR", "LRD", "LSL", "LYD", "MAD", "MDL", "MGA",
    "MKD", "MMK", "MNT", "MOP", "MRU", "MUR", "MVR", "MWK", "MXN", "MXV",
    "MYR", "MZN", "NAD", "NGN", "NIO", "NOK", "NPR", "NZD", "OMR", "PAB",
    "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RON", "RSD", "RUB",
    "RWF", "SAR", "SBD", "SCR", "SDG", "SEK", "SGD", "SHP", "SLE", "SLL",
    "SOS", "SRD", "SSP", "STN", "SVC", "SYP", "SZL", "THB", "TJS", "TMT",
    "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "USN",
    "UYI", "UYU", "UYW", "UZS", "VED", "VES", "VND", "VUV", "WST", "XAF",
    "XAG", "XAU", "XBA", "XBB", "XBC", "XBD", "XCD", "XDR", "XOF", "XPD",
    "XPF", "XPT", "XSU", "XTS", "XUA", "XXX", "YER", "ZAR", "ZMW", "ZWL",
]);

const SESSION_CURRENCY_KEY = "tripcart.ga4.currency";
const PURCHASE_DEDUP_PREFIX = "tripcart.ga4.purchased.";

const isDev = () => {
    try {
        return Boolean(import.meta && import.meta.env && import.meta.env.DEV);
    } catch (_e) {
        return false;
    }
};

const warn = (message, context) => {
    if (isDev() && typeof console !== "undefined" && console.warn) {
        console.warn(`[ga4] ${message}`, context);
    }
};

const isValidCurrency = (code) => typeof code === "string" && ISO_4217.has(code.toUpperCase());

const isValidValue = (value) => typeof value === "number" && Number.isFinite(value);

const isValidItemId = (id) => typeof id === "string" && id.length > 0;

const pruneOptional = (obj) => {
    if (!obj || typeof obj !== "object") {
        return obj;
    }
    const out = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === undefined || value === "") {
            continue;
        }
        out[key] = value;
    }
    return out;
};

const sessionCurrency = () => {
    try {
        return window.sessionStorage.getItem(SESSION_CURRENCY_KEY);
    } catch (_e) {
        return null;
    }
};

const setSessionCurrency = (code) => {
    try {
        window.sessionStorage.setItem(SESSION_CURRENCY_KEY, code);
    } catch (_e) {
        // sessionStorage unavailable — silently skip the assertion
    }
};

// A purchase must be counted exactly once per booking. The booking id
// (transaction_id) is recorded in localStorage so a checkout-page reload — or
// any later revisit of the success page in the same browser — is suppressed.
const purchaseAlreadyFired = (transactionId) => {
    if (!transactionId) {
        return false;
    }
    try {
        return window.localStorage.getItem(PURCHASE_DEDUP_PREFIX + transactionId) !== null;
    } catch (_e) {
        return false;
    }
};

const markPurchaseFired = (transactionId) => {
    if (!transactionId) {
        return;
    }
    try {
        window.localStorage.setItem(PURCHASE_DEDUP_PREFIX + transactionId, "1");
    } catch (_e) {
        // localStorage unavailable — dedup degrades to per-page-load only
    }
};

const ensureDataLayer = () => {
    if (typeof window === "undefined") {
        return null;
    }
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
};

const buildItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }
    const out = [];
    for (const raw of items) {
        if (!raw || typeof raw !== "object") {
            continue;
        }
        if (!isValidItemId(raw.item_id)) {
            warn("dropping item with invalid item_id", raw);
            continue;
        }
        if (!isValidValue(Number(raw.price))) {
            warn("dropping item with invalid price", raw);
            continue;
        }
        out.push(pruneOptional({
            ...raw,
            price: Number(raw.price),
            quantity: raw.quantity !== undefined ? Number(raw.quantity) : undefined,
        }));
    }
    return out;
};

const isValidMeasurementId = (id) =>
    typeof id === "string" && /^G-[A-Z0-9]{4,20}$/.test(id.toUpperCase());

const pushEvent = (eventName, payload) => {
    if (typeof window === "undefined") {
        return false;
    }
    if (window.__GA4_DISABLED__) {
        return false;
    }
    if (!payload || typeof payload !== "object") {
        return false;
    }

    // measurement_id is optional. When present and valid it rides along so our GTM
    // container routes the event to the agency's GA4 property. When absent (GA4
    // disabled in Tools), the event still fires — an agency running their own GTM
    // container via custom scripts consumes the same dataLayer push.
    const rawMeasurementId = typeof payload.measurement_id === "string" ? payload.measurement_id.toUpperCase() : null;
    const measurementId = rawMeasurementId && isValidMeasurementId(rawMeasurementId) ? rawMeasurementId : null;
    if (rawMeasurementId && !measurementId) {
        warn(`${eventName}: ignoring invalid measurement_id`, payload.measurement_id);
    }

    const currency = typeof payload.currency === "string" ? payload.currency.toUpperCase() : null;
    if (!isValidCurrency(currency)) {
        warn(`suppressing ${eventName}: invalid currency`, payload.currency);
        return false;
    }

    const existing = sessionCurrency();
    if (existing && existing !== currency) {
        warn(`suppressing ${eventName}: currency ${currency} differs from session ${existing}`);
        return false;
    }

    // Fire purchase exactly once per booking, regardless of payment type. A
    // missing transaction_id can't be deduplicated, so it's dropped outright.
    if (eventName === "purchase") {
        if (!payload.transaction_id) {
            warn("suppressing purchase: missing transaction_id");
            return false;
        }
        if (purchaseAlreadyFired(payload.transaction_id)) {
            warn(`suppressing purchase: already fired for ${payload.transaction_id}`);
            return false;
        }
    }

    const items = buildItems(payload.items);

    // Multi-day tours reach begin_checkout before any traveller is chosen, so the
    // event legitimately carries no items and nothing to price yet. Every other
    // event still requires at least one valid item.
    const itemlessAllowed = eventName === "begin_checkout";
    if (items.length === 0 && !itemlessAllowed) {
        warn(`suppressing ${eventName}: no valid items`);
        return false;
    }

    const hasValue = payload.value !== undefined && payload.value !== null && payload.value !== "";
    const value = hasValue ? Number(payload.value) : undefined;
    if (hasValue && !isValidValue(value)) {
        warn(`suppressing ${eventName}: invalid value`, payload.value);
        return false;
    }
    if (items.length > 0 && value === undefined) {
        warn(`suppressing ${eventName}: items present but no value`, payload.value);
        return false;
    }

    const ecommerce = pruneOptional({
        currency,
        value,
        transaction_id: payload.transaction_id,
        tax: payload.tax,
        shipping: payload.shipping,
        coupon: payload.coupon,
        items,
    });

    // Custom (non-reserved) event params live top-level alongside `ecommerce` so
    // a GTM Data Layer Variable can read them and map them as event parameters.
    const customParams = pruneOptional({
        package: payload.package,
        destination: payload.destination,
        activity: payload.activity,
    });

    const dataLayer = ensureDataLayer();
    if (!dataLayer) {
        return false;
    }

    const message = { event: eventName, ecommerce, ...customParams };
    if (measurementId) {
        message.measurement_id = measurementId;
    }

    dataLayer.push({ ecommerce: null });
    dataLayer.push(message);

    if (!existing) {
        setSessionCurrency(currency);
    }

    if (eventName === "purchase") {
        markPurchaseFired(payload.transaction_id);
    }

    return true;
};

export const trackViewItem = (payload) => pushEvent("view_item", payload);
export const trackBeginCheckout = (payload) => pushEvent("begin_checkout", payload);
export const trackPurchase = (payload) => pushEvent("purchase", payload);

export const __testing = { isValidCurrency, isValidValue, isValidItemId, isValidMeasurementId, pruneOptional };
